import React, { memo, useMemo, useState } from 'react'
import { Box, Button, MenuItem, Tooltip, Switch, FormControlLabel, CircularProgress } from '@material-ui/core'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { Channel } from 'lib/types/channels'
import { CSS_COLOR_NAMES, NamedColor } from 'lib/colors/cssColorNames'
import { LANGUAGES, LANGUAGE_LABEL_BY_CODE } from 'lib/types/languages'
import styled from '@emotion/styled'

import ConfirmDeleteModal from './ConfirmDeleteModal'

import {
  Section,
  Body,
  CardsGrid,
  ChannelCard,
  ChannelCardHeader,
  ChannelName,
  ChannelNameWrap,
  ChannelDeleteButton,
  ChannelRxIcon,
  Rows,
  EmptyRow,
  Row,
  Left,
  NameWrap,
  RowDeleteButton,
  ColorCol,
  Mid,
  Meta,
  ViewButton,
  ColorSelect,
  LanguageSelect,
  SwatchBox,
  EveryoneRow,
  EveryoneBanner,
  EveryoneTitle,
  EveryoneMeta,
} from './VoiceCards.styles'
import { isAdmin, isSuperAdmin } from 'lib/utils/auth'
import { canUseVoice } from 'lib/utils/licenses'
import { useGlobal } from 'contexts/GlobalProvider'
import { ConditionalRender } from 'components/widgets'
import { useVoice } from 'contexts/VoiceProvider'
import { BRAND_COLOR } from 'lib/constants'

const EVERYONE_CHANNEL_NAME = 'everyone'

function isEveryoneChannelName(name?: string | null): boolean {
  return (
    String(name || '')
      .trim()
      .toLowerCase() === EVERYONE_CHANNEL_NAME
  )
}

export type VoiceGroupViewModel = {
  id: string
  baseChannelId: string
  baseChannelName: string
  language: string
  deviceGroupName: string
  linkedChannelName: string
  deviceCount: number
  channel: Channel
  color?: string
  wifiNetId?: string
}

function languageLabel(lang?: string): string {
  const code = String(lang || '').trim()
  if (!code) return ''
  return LANGUAGE_LABEL_BY_CODE[code] || code
}

type Props = {
  channels: Channel[]
  groups: VoiceGroupViewModel[]

  onViewDevices: (group: VoiceGroupViewModel) => void

  onAddGroup?: (channel: Channel) => void | Promise<void>
  onColorChange?: (group: VoiceGroupViewModel, hex: string | null) => void | Promise<void>
  onLanguageChange?: (group: VoiceGroupViewModel, language: string) => void | Promise<void>

  onDeleteGroup?: (group: VoiceGroupViewModel) => void | Promise<void>
  onDeleteChannel?: (channel: Channel) => void | Promise<void>

  deletingGroupId?: string | null
  deletingChannelName?: string | null
}

function Swatch({ hex }: { hex?: string }) {
  return <SwatchBox style={{ background: hex || 'transparent' }} />
}

export const BrandSwitch = styled(Switch)<{ brandcolor: string }>`
  .MuiSwitch-switchBase.Mui-checked {
    color: ${p => p.brandcolor};
  }

  .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
    background-color: ${p => p.brandcolor};
    opacity: 1;
  }

  .MuiSwitch-track {
    opacity: 0.35;
  }
`

function VoiceCards({
  channels,
  groups,
  onViewDevices,

  onAddGroup,
  onColorChange,
  onLanguageChange,

  onDeleteGroup,
  onDeleteChannel,
  deletingGroupId = null,
  deletingChannelName = null,
}: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmTitle, setConfirmTitle] = useState('Confirm delete')
  const [confirmMessage, setConfirmMessage] = useState<React.ReactNode>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)
  const [confirmAction, setConfirmAction] = useState<(() => Promise<void>) | null>(null)

  const { selectedOrgId, selectedOrg, currentUserV5, licenses } = useGlobal()
  const { isListening, toggleListening, isReceiving, isChannelReady, zelloState } = useVoice()
  const sessionConnectionFailed = Boolean(zelloState.connectionFailed)

  const userId = (currentUserV5 as any)?.id || (currentUserV5 as any)?.userId || null
  const listenDisabled = !canUseVoice({ userId, orgId: selectedOrgId, licenses, superAdmin: isSuperAdmin() })

  const isUltimate = String((selectedOrg as any)?.subscriptionTier || '').toLowerCase() === 'ultimate'

  function openConfirm(args: { title: string; message: React.ReactNode; onConfirm: () => void | Promise<void> }) {
    setConfirmTitle(args.title)
    setConfirmMessage(args.message)
    setConfirmBusy(false)
    setConfirmAction(() => async () => {
      setConfirmBusy(true)
      try {
        await args.onConfirm()
        setConfirmOpen(false)
      } finally {
        setConfirmBusy(false)
      }
    })
    setConfirmOpen(true)
  }

  const { everyoneCard, regularCards } = useMemo(() => {
    const byName = new Map<string, VoiceGroupViewModel[]>()
    for (const g of groups || []) {
      const key = String(g.baseChannelName || '').trim()
      if (!key) continue
      const list = byName.get(key) || []
      list.push(g)
      byName.set(key, list)
    }

    const channelList = Array.isArray(channels) ? channels : []
    const entries = channelList
      .map(ch => {
        const name = String((ch as any)?.name ?? '').trim()
        if (!name) return null
        const list = byName.get(name) || []
        list.sort((a, b) => String(a.deviceGroupName || '').localeCompare(String(b.deviceGroupName || '')))
        return {
          channel: ch,
          channelName: name,
          list,
          isEveryone: isEveryoneChannelName(name),
        }
      })
      .filter(Boolean) as Array<{
      channel: Channel
      channelName: string
      list: VoiceGroupViewModel[]
      isEveryone: boolean
    }>

    const everyoneEntry = entries.find(entry => entry.isEveryone) || null
    const normalEntries = entries
      .filter(entry => !entry.isEveryone)
      .sort((a, b) => a.channelName.localeCompare(b.channelName))

    return {
      everyoneCard: everyoneEntry,
      regularCards: normalEntries,
    }
  }, [channels, groups])

  async function handleColorSelect(g: VoiceGroupViewModel, hex: string) {
    const next = hex || ''
    try {
      await onColorChange?.(g, next ? next : null)
    } catch (e) {
      console.warn('Color change handler error (non-fatal):', e)
    }
  }

  async function handleLanguageSelect(g: VoiceGroupViewModel, lang: string) {
    if (g.language === lang) return
    try {
      await onLanguageChange?.(g, lang)
    } catch (e) {
      console.warn('Language change handler error (non-fatal):', e)
    }
  }

  function requestDeleteGroup(g: VoiceGroupViewModel) {
    if (!onDeleteGroup) return
    if (g.deviceCount > 0) return

    openConfirm({
      title: 'Delete group?',
      message: (
        <>
          {'Delete '}
          <span style={{ fontWeight: 900 }}>{String(g.deviceGroupName || '').trim() || 'this group'}</span>
          {' from '}
          <span style={{ fontWeight: 900 }}>{g.baseChannelName}</span>
          {'? This cannot be undone.'}
        </>
      ),
      onConfirm: async () => {
        await onDeleteGroup(g)
      },
    })
  }

  function requestDeleteChannel(ch: Channel, channelName: string, listLen: number) {
    if (!onDeleteChannel) return
    if (listLen > 0) return
    if (isEveryoneChannelName(channelName)) return

    openConfirm({
      title: 'Delete channel?',
      message: (
        <>
          {'Delete channel '}
          <span style={{ fontWeight: 900 }}>{channelName}</span>
          {'? This cannot be undone.'}
        </>
      ),
      onConfirm: async () => {
        await onDeleteChannel(ch)
      },
    })
  }

  return (
    <>
      <Section>
        <Body>
          {everyoneCard && (
            <EveryoneRow>
              <EveryoneBanner>
                <EveryoneTitle>{everyoneCard.channelName}</EveryoneTitle>
                <EveryoneMeta>{'All devices · Always connected'}</EveryoneMeta>
              </EveryoneBanner>
            </EveryoneRow>
          )}

          <CardsGrid>
            {regularCards.map(({ channel, channelName, list }) => {
              const canDeleteChannel = list.length === 0 && isAdmin(selectedOrgId)
              const channelDeleting = String(deletingChannelName || '') === String(channelName || '')
              const listening = isListening(channel)
              const receiving = isReceiving(channel)
              const channelReady = isChannelReady(channelName)
              // listenFailed: user wanted to listen, but the watchdog
              // declared the session failed before this channel reported
              // online. UI swaps the toggle for an error indicator.
              const listenFailed = listening && !channelReady && sessionConnectionFailed
              // listenConnecting: user wants to listen, channel hasn't
              // emitted on_channel_status yet, and we're still hopeful
              // (no failure yet). UI swaps the toggle for a loader.
              const listenConnecting = listening && !channelReady && !listenFailed

              return (
                <ChannelCard key={channelName} receiving={receiving}>
                  <ChannelCardHeader receiving={receiving}>
                    <ChannelNameWrap>
                      {receiving && (
                        <ChannelRxIcon>
                          <VolumeUpIcon fontSize="small" />
                        </ChannelRxIcon>
                      )}

                      <ChannelName title={channelName} receiving={receiving}>
                        {channelName}
                      </ChannelName>

                      {canDeleteChannel && (
                        <Tooltip title="Delete channel">
                          <ChannelDeleteButton
                            className="channel-delete"
                            size="small"
                            disabled={channelDeleting || confirmBusy}
                            onClick={e => {
                              e.stopPropagation()
                              requestDeleteChannel(channel, channelName, list.length)
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </ChannelDeleteButton>
                        </Tooltip>
                      )}
                    </ChannelNameWrap>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Tooltip
                        title={
                          listenDisabled
                            ? 'You need a Voice License to join voice channels.'
                            : listenFailed
                            ? 'Connection failed — refresh to retry'
                            : listenConnecting
                            ? 'Connecting…'
                            : ''
                        }
                      >
                        <span>
                          <FormControlLabel
                            control={
                              listenFailed ? (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 38,
                                    height: 22,
                                    marginLeft: 12,
                                    marginRight: 8,
                                    color: '#d32f2f',
                                  }}
                                >
                                  <ErrorOutlineIcon fontSize="small" />
                                </span>
                              ) : listenConnecting ? (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 38,
                                    height: 22,
                                    marginLeft: 12,
                                    marginRight: 8,
                                  }}
                                >
                                  <CircularProgress size={16} thickness={5} style={{ color: BRAND_COLOR }} />
                                </span>
                              ) : (
                                <BrandSwitch
                                  brandcolor={BRAND_COLOR}
                                  checked={listening}
                                  disabled={listenDisabled}
                                  onClick={e => e.stopPropagation()}
                                  onChange={async e => {
                                    e.stopPropagation()
                                    await toggleListening(channel)
                                  }}
                                  size="small"
                                />
                              )
                            }
                            label={listenFailed ? 'Failed' : 'Listen'}
                            style={{ marginRight: 0 }}
                          />
                        </span>
                      </Tooltip>

                      <ConditionalRender condition={isAdmin(selectedOrgId)}>
                        <Tooltip
                          title={
                            isUltimate
                              ? ''
                              : 'Language Translation is an Ultimate Tier feature. Please contact your Client Success Manager for more information or to upgrade your subscription and access this feature.'
                          }
                        >
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!isUltimate}
                              onClick={() => onAddGroup?.(channel)}
                            >
                              Add Language
                            </Button>
                          </span>
                        </Tooltip>
                      </ConditionalRender>
                    </div>
                  </ChannelCardHeader>

                  <Rows>
                    {list.length === 0 ? (
                      <EmptyRow>{'No groups yet.'}</EmptyRow>
                    ) : (
                      list.map(g => {
                        const selectedColor = String(g.color || '')

                        const canDeleteGroup = g.deviceCount === 0 && isAdmin(selectedOrgId)
                        const groupDeleting = String(deletingGroupId || '') === String(g.id || '')

                        return (
                          <Row key={g.id}>
                            <Left>
                              <NameWrap>
                                {isUltimate ? (
                                  <LanguageSelect
                                    value={g.language}
                                    onChange={e => handleLanguageSelect(g, String(e.target.value))}
                                    variant="outlined"
                                    MenuProps={{
                                      // Same 59-item list as the Add Language dialog,
                                      // and the same fix: without `getContentAnchorEl:
                                      // null` the menu is placed so the selected
                                      // language sits over the pill, which moves it
                                      // unpredictably as the selection changes.
                                      getContentAnchorEl: null,
                                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                                      PaperProps: { style: { maxHeight: 320 } },
                                    }}
                                    disabled={!isAdmin(selectedOrgId)}
                                    title={languageLabel(String(g.language || ''))}
                                    renderValue={(val: any) => (
                                      <span style={{ textTransform: 'none' }}>{languageLabel(String(val || ''))}</span>
                                    )}
                                  >
                                    {LANGUAGES.map(l => (
                                      <MenuItem key={l.code} value={l.code}>
                                        {`${l.label} — `}
                                        <span style={{ textTransform: 'none' }}>{l.code}</span>
                                      </MenuItem>
                                    ))}
                                  </LanguageSelect>
                                ) : (
                                  <span style={{ fontStyle: 'italic', color: 'rgba(0,0,0,0.55)', fontSize: 13 }}>
                                    Default Language
                                  </span>
                                )}

                                {/* Always-reserved slot so the pill width is identical on every
                                    row, whether or not the delete button is shown. */}
                                <span style={{ flex: '0 0 28px', display: 'flex', justifyContent: 'center' }}>
                                  {canDeleteGroup && (
                                    <Tooltip title="Delete group">
                                      <RowDeleteButton
                                        className="row-delete"
                                        size="small"
                                        disabled={groupDeleting || confirmBusy}
                                        onClick={e => {
                                          e.stopPropagation()
                                          requestDeleteGroup(g)
                                        }}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </RowDeleteButton>
                                    </Tooltip>
                                  )}
                                </span>
                              </NameWrap>
                            </Left>

                            <ColorCol>
                              <ColorSelect
                                value={selectedColor}
                                onChange={e => handleColorSelect(g, String(e.target.value))}
                                variant="outlined"
                                displayEmpty
                                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } as any }}
                                renderValue={(val: any) =>
                                  val ? <Swatch hex={String(val)} /> : <Swatch hex="transparent" />
                                }
                                disabled={!isAdmin(selectedOrgId)}
                              >
                                <MenuItem value="">
                                  <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                                    <Swatch hex="transparent" />
                                    <em>Default (no color)</em>
                                  </Box>
                                </MenuItem>

                                {CSS_COLOR_NAMES.map((c: NamedColor) => (
                                  <MenuItem key={c.hex} value={c.hex}>
                                    <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                                      <Swatch hex={c.hex} />
                                      <span>{c.name}</span>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </ColorSelect>
                            </ColorCol>

                            <Mid>
                              <Meta title={`${g.deviceCount} devices`}>{`${g.deviceCount} devices`}</Meta>
                              <ViewButton
                                variant="outlined"
                                onClick={() => onViewDevices(g)}
                                style={{ borderColor: 'rgba(0,0,0,0.25)', color: 'rgba(0,0,0,0.8)' }}
                              >
                                View
                              </ViewButton>
                            </Mid>
                          </Row>
                        )
                      })
                    )}
                  </Rows>
                </ChannelCard>
              )
            })}
          </CardsGrid>
        </Body>
      </Section>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        busy={confirmBusy}
        confirmText="Delete"
        cancelText="Cancel"
        onClose={() => !confirmBusy && setConfirmOpen(false)}
        onConfirm={async () => {
          if (!confirmAction) return
          await confirmAction()
        }}
      />
    </>
  )
}

export default memo(VoiceCards)
